import { forwardRef, Fragment, useImperativeHandle, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, DialogTitle, Link } from '@mui/material';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsDialog } from '../common/basic/QcsDialog';
import { QcsDialogActions } from '../common/basic/QcsDialogActions';
import { QcsDialogContentText } from '../common/basic/QcsDialogContentText';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { UserState } from '@api/models/enums/UserState';
import { SubcontractorRequestDto } from '@api/models/suppliers/SubcontractorRequestDto';

type ConfirmAction = 'cancel' | 'confirm' | 'redirect';

export interface SubcontractorLendConfirmHandle {
  // Opens the dialog and resolves once the user picks an action.
  confirm: () => Promise<ConfirmAction>;
  // Navigates to the requested employee(s); call after the save succeeds.
  goToRequests: () => void;
}

interface Props {
  requests: SubcontractorRequestDto[];
  // Id of the supplier being edited — the requested employees belong to it.
  supplierId: string;
}

export const SubcontractorLendConfirmDialog = forwardRef<SubcontractorLendConfirmHandle, Props>(({ requests, supplierId }, ref) => {
  const { t } = useTranslation();
  const { navigate, getPath } = useNavigation();
  const [open, setOpen] = useState(false);
  const resolveRef = useRef<((action: ConfirmAction) => void) | null>(null);

  const isSingle = requests.length === 1;

  const resolve = (action: ConfirmAction) => {
    setOpen(false);
    resolveRef.current?.(action);
    resolveRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    confirm: () =>
      new Promise<ConfirmAction>((res) => {
        resolveRef.current = res;
        setOpen(true);
      }),
    goToRequests: () => {
      if (isSingle) {
        navigate(PathKey.SupplierEmployee, { supplierId, employeeId: requests[0].requestedEmployee?.id ?? '' });
      } else {
        navigate(PathKey.SupplierEmployees, { supplierId }, { state: UserState.Requested });
      }
    },
  }));

  // A comma-separated list of clickable links to the requesting suppliers' detail pages.
  const supplierLinks = requests.map((request, index) => (
    <Fragment key={request.id ?? index}>
      {index > 0 && ', '}
      <Link component={RouterLink} to={getPath(PathKey.SupplierDetail, { supplierId: request.requestingSupplier?.id ?? '' })}>
        {request.requestingSupplier?.name}
      </Link>
    </Fragment>
  ));

  // A comma-separated list of clickable links to the requested employees' detail pages.
  const employeeLinks = requests.map((request, index) => (
    <Fragment key={request.requestedEmployee?.id ?? index}>
      {index > 0 && ', '}
      <Link component={RouterLink} to={getPath(PathKey.SupplierEmployee, { supplierId, employeeId: request.requestedEmployee?.id ?? '' })}>
        {request.requestedEmployee?.name}
      </Link>
    </Fragment>
  ));

  return (
    <QcsDialog open={open} onClose={() => resolve('cancel')} maxWidth="md" fullWidth>
      <DialogTitle>{t('supplier.subcontractorLendConfirm.title')}</DialogTitle>
      <QcsDialogContentText sx={{ px: 1, py: 1, fontSize: '1rem', lineHeight: 1.7 }}>
        <Trans
          i18nKey={isSingle ? 'supplier.subcontractorLendConfirm.single' : 'supplier.subcontractorLendConfirm.multiple'}
          values={{ field: t('supplier.canLendEmployees') }}
          components={{
            suppliers: <Box component="span">{supplierLinks}</Box>,
            employees: <Box component="span">{employeeLinks}</Box>,
          }}
        />
      </QcsDialogContentText>
      <QcsDialogActions>
        <QcsButton onClick={() => resolve('cancel')}>{t('common.cancel')}</QcsButton>
        <QcsButton onClick={() => resolve('redirect')}>
          {isSingle ? t('supplier.subcontractorLendConfirm.redirectSingle') : t('supplier.subcontractorLendConfirm.redirectMultiple')}
        </QcsButton>
        <QcsButton variant="contained" onClick={() => resolve('confirm')}>
          {t('common.confirm')}
        </QcsButton>
      </QcsDialogActions>
    </QcsDialog>
  );
});

SubcontractorLendConfirmDialog.displayName = 'SubcontractorLendConfirmDialog';
